import { supabase } from '@/lib/supabase'

export type MasterProfileContact = {
  id: string
  full_name: string | null
  email: string | null
  avatar_url: string | null
  role: string | null
}

export type VisionModeratorUser = {
  id: string
  fullName: string
  email: string
  avatarUrl: string | null
  role: string
  createdAt: string
}

function isMissingAvatarUrlColumnError(error: any): boolean {
  const msg = String(error?.message || '')
  return msg.includes('v_profiles_contact_master.avatar_url') && msg.includes('does not exist')
}

function normalizeMasterContacts(rowsRaw: any): MasterProfileContact[] {
  const rows = Array.isArray(rowsRaw) ? rowsRaw : []

  return rows
    .map((row: any) => {
      const id = row?.id ? String(row.id) : ''
      if (!id) return null

      return {
        id,
        full_name:
          typeof row?.full_name === 'string' || row?.full_name === null
            ? (row.full_name as string | null)
            : null,
        email:
          typeof row?.email === 'string' || row?.email === null
            ? (row.email as string | null)
            : null,
        avatar_url:
          typeof row?.avatar_url === 'string' || row?.avatar_url === null
            ? (row.avatar_url as string | null)
            : null,
        role:
          typeof row?.role === 'string' || row?.role === null
            ? (row.role as string | null)
            : null,
      }
    })
    .filter(Boolean) as MasterProfileContact[]
}

export async function listVisionModerators(): Promise<VisionModeratorUser[]> {
  const { data: rows, error } = await supabase
    .from('vision_moderators')
    .select('user_id,created_at')
    .order('created_at', { ascending: false })

  if (error) throw error

  const ids = Array.from(
    new Set(
      (rows ?? [])
        .map((r: any) => (r?.user_id ? String(r.user_id) : ''))
        .filter(Boolean),
    ),
  )

  if (ids.length === 0) return []

  let { data: profs, error: profErr } = await supabase
    .from('v_profiles_contact_master')
    .select('id,full_name,email,avatar_url,role')
    .in('id', ids as any)

  if (profErr && isMissingAvatarUrlColumnError(profErr)) {
    const { data, error } = await supabase
      .from('v_profiles_contact_master')
      .select('id,full_name,email,role')
      .in('id', ids as any)

    profs = (data ?? []).map((p: any) => ({ ...p, avatar_url: null }))
    profErr = error
  }

  if (profErr) throw profErr

  const normalized = normalizeMasterContacts(profs)
  const byId = new Map<string, MasterProfileContact>(
    normalized.map((p: MasterProfileContact) => [String(p.id), p]),
  )

  return (rows ?? []).map((r: any) => {
    const id = r?.user_id ? String(r.user_id) : ''
    const profile = byId.get(id)

    return {
      id,
      fullName: String(profile?.full_name ?? '').trim(),
      email: String(profile?.email ?? '').trim(),
      avatarUrl:
        typeof profile?.avatar_url === 'string' || profile?.avatar_url === null
          ? (profile?.avatar_url as string | null)
          : null,
      role: String(profile?.role ?? '').trim(),
      createdAt: r?.created_at ? String(r.created_at) : '',
    }
  })
}

export async function searchMasterUsers(params: {
  query: string
  limit?: number
}): Promise<MasterProfileContact[]> {
  const query = String(params.query || '').trim()
  const limit = Math.max(1, Math.min(Number(params.limit) || 8, 25))
  if (!query) return []

  const s = `%${query}%`

  const baseQuery = (select: string) =>
    supabase
      .from('v_profiles_contact_master')
      .select(select)
      .or(`full_name.ilike.${s},email.ilike.${s}`)
      .order('created_at', { ascending: false })
      .limit(limit)

  let { data, error } = await baseQuery('id,full_name,email,avatar_url,role')

  if (error && isMissingAvatarUrlColumnError(error)) {
    const res = await baseQuery('id,full_name,email,role')
    data = (res.data ?? []).map((p: any) => ({ ...p, avatar_url: null }))
    error = res.error
  }

  if (error) throw error
  return normalizeMasterContacts(data)
}

export async function addVisionModerator(userId: string): Promise<void> {
  const id = String(userId || '').trim()
  if (!id) return

  const { error } = await supabase
    .from('vision_moderators')
    .upsert({ user_id: id }, { onConflict: 'user_id' })

  if (error) throw error
}

export async function removeVisionModerator(userId: string): Promise<void> {
  const id = String(userId || '').trim()
  if (!id) return

  const { error } = await supabase
    .from('vision_moderators')
    .delete()
    .eq('user_id', id)

  if (error) throw error
}
